import moment from "moment";
import "moment/locale/th";

// ---- Option builders ----

/**
 * Returns an array of 24 monthly options (current month first), using moment's
 * Thai locale for the display text, matching ClinicDashboard.vue's buildMonthOptions().
 * @returns {{ value: string, text: string }[]}  value = "YYYY-MM", text = "MMMM YYYY" (th)
 */
export function buildMonthOptions() {
  moment.locale("th");
  const options = [];
  for (let i = 0; i < 24; i++) {
    const date = moment().subtract(i, "months");
    options.push({
      value: date.format("YYYY-MM"),
      text: date.format("MMMM YYYY"),
    });
  }
  return options;
}

/**
 * Returns an array of 3 yearly options (current year first), with Buddhist Era
 * text, matching ClinicDashboard.vue's buildYearOptions().
 * @returns {{ value: string, text: string }[]}  value = "YYYY", text = "ปี <BE>"
 */
export function buildYearOptions() {
  const currentYear = moment().year();
  const years = [];
  for (let i = 0; i < 3; i++) {
    const year = currentYear - i;
    years.push({ value: year.toString(), text: `ปี ${year + 543}` });
  }
  return years;
}

// ---- Date range ----

/**
 * Returns { startDt, endDt } as ISO strings (moment().format() — includes local +07 offset)
 * for the given viewType and selection parameters.
 *
 * @param {"month"|"year"|"day"} viewType
 * @param {{ month?: string, year?: string, day?: string }} params
 * @returns {{ startDt: string, endDt: string }}
 */
export function rangeFor(viewType, { month, year, day } = {}) {
  if (viewType === "year") {
    return {
      startDt: moment(year).startOf("year").format(),
      endDt: moment(year).endOf("year").format(),
    };
  }
  if (viewType === "day") {
    return {
      startDt: moment(day).startOf("day").format(),
      endDt: moment(day).endOf("day").format(),
    };
  }
  // default: "month"
  return {
    startDt: moment(month).startOf("month").format(),
    endDt: moment(month).endOf("month").format(),
  };
}

// ---- Comparison labels ----

/**
 * Returns Thai Buddhist-Era comparison period labels for display in the toggle buttons.
 *
 * Buddhist short year = (gregorianYear + 543) % 100  (2-digit)
 *
 * @param {"month"|"year"|"day"} viewType
 * @param {{ month?: string, year?: string, day?: string }} params
 * @returns {{ prevPeriodLabel: string, prevYearLabel: string }}
 */
export function comparisonLabels(viewType, { month, year, day } = {}) {
  if (viewType === "day") {
    const prevDay = moment(day).subtract(1, "day");
    // มุมมองรายวันเทียบกับ "วันเดียวกันสัปดาห์ก่อน" (−7 วัน) เช่น อังคาร → อังคารที่แล้ว
    const lastWeek = moment(day).subtract(7, "day").locale("th");
    const prevBEshort = (prevDay.year() + 543) % 100;
    const lastWeekBEshort = (lastWeek.year() + 543) % 100;
    return {
      prevPeriodLabel:
        "เมื่อวาน " + prevDay.locale("th").format("D MMM") + " " + prevBEshort,
      prevYearLabel:
        lastWeek.format("dddd") + "ที่แล้ว " + lastWeek.format("D MMM") + " " + lastWeekBEshort,
    };
  }

  if (viewType === "year") {
    const yearNum = parseInt(year, 10);
    const prevYearNum = yearNum - 1;
    const prevYearBE = prevYearNum + 543;
    return {
      prevPeriodLabel: "ปี " + prevYearBE,
      prevYearLabel: "ปี " + prevYearBE,
    };
  }

  // default: "month"
  const prevMonth = moment(month).subtract(1, "month");
  const sameMonthLastYear = moment(month).subtract(1, "year");
  const prevBEshort = (prevMonth.year() + 543) % 100;
  const lastYearBEshort = (sameMonthLastYear.year() + 543) % 100;
  return {
    prevPeriodLabel:
      "เดือน " + prevMonth.locale("th").format("MMM") + " " + prevBEshort,
    prevYearLabel:
      "เดือน " + sameMonthLastYear.locale("th").format("MMM") + " " + lastYearBEshort,
  };
}
